import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Evaluator } from '../../lib/supabase';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import AdminPromotionForm from '../Auth/AdminPromotionForm';

export default function EvaluatorProfile() {
  const [evaluator, setEvaluator] = useState<Evaluator | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [emailForm, setEmailForm] = useState({ newEmail: '' });
  const [pwdForm, setPwdForm] = useState({ current: '', next: '', confirm: '' });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [myThreads, setMyThreads] = useState<{
    candidate_id: string;
    candidate_name: string;
    research_topic: string;
    topic: string;
    comment_count: number;
    last_commented_at: string;
  }[]>([]);
  const [likedList, setLikedList] = useState<{ candidate_id: string; candidate_name: string; research_topic: string }[]>([]);
  const [participateList, setParticipateList] = useState<{ candidate_id: string; candidate_name: string; research_topic: string }[]>([]);
  const [showAdminPromotion, setShowAdminPromotion] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [wasAdminMode, setWasAdminMode] = useState(false);

  const [editData, setEditData] = useState({
    name: '',
    username: '',
    affiliation: '',
    department: '',
    is_grad_student: false,
    grad_program: '',
    grad_year: ''
  });

  useEffect(() => {
    loadEvaluator();
    loadMyThreads();
  }, []);

  useEffect(() => {
    if (evaluator?.id) {
      loadPreferences(evaluator.id);
    }
  }, [evaluator?.id]);

  // Admin flag and previous mode flag must be set via hook at top-level
  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: adminRow } = await supabase.from('admin_users').select('user_id').eq('user_id', user.id).maybeSingle();
        setIsAdmin(!!adminRow);
      }
      try { setWasAdminMode(!!localStorage.getItem('was_admin_mode')); } catch {}
    })();
  }, []);

  const loadMyThreads = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Fetch all comments authored by current user
      const { data: myComments, error: cErr } = await supabase
        .from('evaluation_comments')
        .select('evaluation_id, topic, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (cErr) throw cErr;
      if (!myComments || myComments.length === 0) {
        setMyThreads([]);
        return;
      }

      const evalIds = Array.from(new Set(myComments.map(c => c.evaluation_id)));
      // Map evaluation -> candidate
      const { data: evals, error: eErr } = await supabase
        .from('evaluations')
        .select('id, candidate_id')
        .in('id', evalIds);
      if (eErr) throw eErr;
      const evalToCandidate = new Map<string, string>(
        (evals || []).map((e: any) => [e.id, e.candidate_id])
      );

      const candidateIds = Array.from(new Set((evals || []).map((e: any) => e.candidate_id)));
      const { data: candidates, error: candErr } = await supabase
        .from('candidates')
        .select('id, name, research_topic')
        .in('id', candidateIds);
      if (candErr) throw candErr;
      const cMap = new Map<string, any>((candidates || []).map((c: any) => [c.id, c]));

      // Group by (candidate_id, topic)
      const groups = new Map<string, { candidate_id: string; topic: string; count: number; last: string }>();
      for (const c of myComments) {
        const candidate_id = evalToCandidate.get(c.evaluation_id as string);
        if (!candidate_id) continue;
        const topic = (c.topic as string) || 'general';
        const key = `${candidate_id}__${topic}`;
        const prev = groups.get(key);
        if (!prev) {
          groups.set(key, { candidate_id, topic, count: 1, last: c.created_at as string });
        } else {
          prev.count += 1;
          if (new Date(c.created_at as string).getTime() > new Date(prev.last).getTime()) {
            prev.last = c.created_at as string;
          }
        }
      }

      const threads: any[] = [];
      for (const g of groups.values()) {
        const cand = cMap.get(g.candidate_id);
        if (!cand) continue;
        threads.push({
          candidate_id: g.candidate_id,
          candidate_name: cand.name,
          research_topic: cand.research_topic,
          topic: g.topic,
          comment_count: g.count,
          last_commented_at: g.last,
        });
      }
      // Sort by last commented desc
      threads.sort((a, b) => new Date(b.last_commented_at).getTime() - new Date(a.last_commented_at).getTime());
      setMyThreads(threads);
    } catch (e: any) {
      console.error(e);
    }
  };

  const loadPreferences = async (evaluatorId: string) => {
    try {
      const { data: likes, error: likeErr } = await supabase
        .from('evaluations')
        .select('candidate_id')
        .eq('evaluator_id', evaluatorId)
        .eq('liked', true);
      if (likeErr) throw likeErr;
      const { data: parts, error: partErr } = await supabase
        .from('evaluations')
        .select('candidate_id')
        .eq('evaluator_id', evaluatorId)
        .eq('want_to_participate', true);
      if (partErr) throw partErr;

      const likeCandidateIds = Array.from(new Set((likes || []).map((r: any) => r.candidate_id)));
      const partCandidateIds = Array.from(new Set((parts || []).map((r: any) => r.candidate_id)));
      const allIds = Array.from(new Set([...likeCandidateIds, ...partCandidateIds]));
      if (allIds.length === 0) {
        setLikedList([]);
        setParticipateList([]);
        return;
      }
      const { data: cands, error: candErr } = await supabase
        .from('candidates')
        .select('id, name, research_topic')
        .in('id', allIds);
      if (candErr) throw candErr;
      const cmap = new Map<string, any>((cands || []).map((c: any) => [c.id, c]));
      setLikedList(likeCandidateIds.map(id => ({ candidate_id: id, candidate_name: cmap.get(id)?.name || '', research_topic: cmap.get(id)?.research_topic || '' })));
      setParticipateList(partCandidateIds.map(id => ({ candidate_id: id, candidate_name: cmap.get(id)?.name || '', research_topic: cmap.get(id)?.research_topic || '' })));
    } catch (e) {
      console.error(e);
    }
  };

  const loadEvaluator = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data, error } = await supabase
        .from('evaluators')
        .select('*')
        .eq('email', user.email)
        .single();
      if (error) throw error;
      setEvaluator(data as Evaluator);
      setEditData({
        name: (data as any).name || '',
        username: (data as any).username || '',
        affiliation: (data as any).affiliation || '',
        department: (data as any).department || '',
        is_grad_student: !!(data as any).is_grad_student,
        grad_program: (data as any).grad_program || '',
        grad_year: (data as any).grad_year ? String((data as any).grad_year) : ''
      });
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!evaluator) return;
    if (!editData.name.trim() || !editData.affiliation.trim()) {
      setError('名前と所属は必須です');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const { error } = await supabase
        .from('evaluators')
        .update({
          name: editData.name.trim(),
          username: editData.username.trim() || null,
          affiliation: editData.affiliation.trim(),
          department: editData.department.trim() || null,
          is_grad_student: !!editData.is_grad_student,
          grad_program: editData.grad_program || null,
          grad_year: editData.grad_year ? parseInt(editData.grad_year) : null
        })
        .eq('id', evaluator.id);
      if (error) throw error;
      await loadEvaluator();
      setIsEditing(false);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('セッションが無効です');
      const resp = await fetch('/api/delete-account', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j.error || '削除に失敗しました');
      }
      await supabase.auth.signOut();
      window.location.reload();
    } catch (e: any) {
      setError(e.message);
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  

  return (
    <div className="space-y-6">
      {error && (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>参加者プロフィール</CardTitle>
            {!isEditing ? (
              <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                編集
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { setIsEditing(false); setError(null); }} disabled={saving}>キャンセル</Button>
                <Button size="sm" onClick={handleSave} disabled={saving}>{saving ? '保存中…' : '保存'}</Button>
              </div>
            )}
          </div>
          <CardDescription>基本情報を編集できます。</CardDescription>
        </CardHeader>
        <CardContent>
          {!isEditing ? (
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                プロフィール情報は折りたたまれています。<span className="hidden md:inline">「編集」を押すとフォームが展開されます。</span>
              </div>
              <div className="text-sm">
                <span className="text-foreground font-medium mr-2">{evaluator?.username || evaluator?.name}</span>
                <span className="text-muted-foreground">/ {(evaluator as any)?.affiliation || '未設定'}</span>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">名前 *</Label>
                  <Input id="name" value={editData.name} onChange={(e) => setEditData({ ...editData, name: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="username">ユーザー名（任意）</Label>
                  <Input id="username" value={editData.username} onChange={(e) => setEditData({ ...editData, username: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="affiliation">所属 *</Label>
                <Input id="affiliation" value={editData.affiliation} onChange={(e) => setEditData({ ...editData, affiliation: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="department">系・部署・診療科（任意）</Label>
                <Input id="department" value={editData.department} onChange={(e) => setEditData({ ...editData, department: e.target.value })} />
              </div>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox id="is-grad" checked={editData.is_grad_student} onCheckedChange={(c) => setEditData({ ...editData, is_grad_student: !!c })} />
                  <Label htmlFor="is-grad" className="text-sm">大学院生（修士/博士）</Label>
                </div>
                {editData.is_grad_student && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="grad-program">課程（博士・修士）</Label>
                      <select id="grad-program" className="w-full border rounded-md h-9 px-3 bg-background" value={editData.grad_program} onChange={(e) => setEditData({ ...editData, grad_program: e.target.value })}>
                        <option value="">選択してください</option>
                        <option value="doctor">博士</option>
                        <option value="master">修士</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="grad-year">学年</Label>
                      <Input id="grad-year" type="number" min="1" max="6" value={editData.grad_year} onChange={(e) => setEditData({ ...editData, grad_year: e.target.value })} placeholder="1" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Account Settings */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>アカウント設定</CardTitle>
            <Button variant="outline" size="sm" onClick={() => setAccountOpen(v => !v)}>
              {accountOpen ? '閉じる' : '開く'}
            </Button>
          </div>
          <CardDescription>メールアドレス・パスワードの変更</CardDescription>
        </CardHeader>
        {accountOpen && (
          <CardContent className="space-y-6">
            {/* Change email */}
            <div className="space-y-2">
              <Label htmlFor="new-email">新しいメールアドレス</Label>
              <Input
                id="new-email"
                type="email"
                placeholder={evaluator?.email || ''}
                value={emailForm.newEmail}
                onChange={(e) => setEmailForm({ newEmail: e.target.value })}
              />
              <div className="flex gap-2">
                <Button
                  onClick={async () => {
                    if (!emailForm.newEmail.trim()) return;
                    setError(null);
                    setEmailSaving(true);
                    try {
                      const { data: { session } } = await supabase.auth.getSession();
                      const token = session?.access_token;
                      if (!token) throw new Error('セッションが無効です');
                      const resp = await fetch('/api/update-email', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({ newEmail: emailForm.newEmail.trim() })
                      });
                      const j = await resp.json();
                      if (!resp.ok) throw new Error(j.error || 'メール更新に失敗しました');
                      await loadEvaluator();
                    } catch (e: any) {
                      setError(e.message);
                    } finally {
                      setEmailSaving(false);
                    }
                  }}
                  disabled={emailSaving || !emailForm.newEmail.trim()}
                >
                  {emailSaving ? '更新中…' : 'メールを更新'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">注意: メール変更後は再ログインが必要になる場合があります。</p>
            </div>

            {/* Change password */}
            <div className="space-y-2">
              <Label htmlFor="cur-pwd">現在のパスワード</Label>
              <Input id="cur-pwd" type="password" value={pwdForm.current} onChange={(e) => setPwdForm({ ...pwdForm, current: e.target.value })} />
              <Label htmlFor="new-pwd" className="mt-2">新しいパスワード</Label>
              <Input id="new-pwd" type="password" value={pwdForm.next} onChange={(e) => setPwdForm({ ...pwdForm, next: e.target.value })} />
              <Label htmlFor="confirm-pwd" className="mt-2">新しいパスワード（確認）</Label>
              <Input id="confirm-pwd" type="password" value={pwdForm.confirm} onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })} />
              <div className="flex gap-2 mt-2">
                <Button
                  onClick={async () => {
                    if (!pwdForm.current || !pwdForm.next || pwdForm.next !== pwdForm.confirm) {
                      setError('パスワードを確認してください');
                      return;
                    }
                    setError(null);
                    setPasswordSaving(true);
                    try {
                      const { data: { user } } = await supabase.auth.getUser();
                      if (!user?.email) throw new Error('ユーザーが見つかりません');
                      const { error: signInErr } = await supabase.auth.signInWithPassword({ email: user.email, password: pwdForm.current });
                      if (signInErr) throw new Error('現在のパスワードが正しくありません');
                      const { error: updErr } = await supabase.auth.updateUser({ password: pwdForm.next });
                      if (updErr) throw updErr;
                      setPwdForm({ current: '', next: '', confirm: '' });
                    } catch (e: any) {
                      setError(e.message);
                    } finally {
                      setPasswordSaving(false);
                    }
                  }}
                  disabled={passwordSaving}
                >
                  {passwordSaving ? '変更中…' : 'パスワードを変更'}
                </Button>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      <Card className="border-destructive/40">
        <CardHeader>
          <CardTitle className="text-destructive">アカウントの削除</CardTitle>
          <CardDescription>この操作は取り消せません。参加者プロフィールやコメントなど関連データも削除されます。</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!confirmDelete ? (
            <Button variant="destructive" onClick={() => setConfirmDelete(true)}>アカウントを削除する</Button>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">本当に削除しますか？この操作は元に戻せません。</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setConfirmDelete(false)} disabled={deleting}>キャンセル</Button>
                <Button variant="destructive" onClick={handleDelete} disabled={deleting}>{deleting ? '削除中…' : '完全に削除する'}</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>管理者になる</CardTitle>
          <CardDescription>管理者は全ユーザーの編集・削除、評価（いいね/参加希望）調整が可能です。</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Button variant="outline" onClick={() => setShowAdminPromotion((v) => !v)}>
              {showAdminPromotion ? '申請フォームを隠す' : '管理者申請'}
            </Button>
            {isAdmin && (
              <Button onClick={() => { localStorage.setItem('goto_admin', '1'); window.location.reload(); }}>
                管理者モードへ
              </Button>
            )}
            {wasAdminMode && (
              <Button variant="ghost" onClick={() => { localStorage.setItem('goto_normal', 'evaluations'); window.location.reload(); }}>
                通常モードへ
              </Button>
            )}
            {showAdminPromotion && (
              <AdminPromotionForm inline onSuccess={() => window.location.reload()} onCancel={() => setShowAdminPromotion(false)} />
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>いいねした発表</CardTitle>
          <CardDescription>あなたが「いいね！」した研究の一覧です。</CardDescription>
        </CardHeader>
        <CardContent>
          {likedList.length === 0 ? (
            <p className="text-sm text-muted-foreground">まだ「いいね！」した発表はありません。</p>
          ) : (
            <div className="space-y-2">
              {likedList.map((r) => (
                <div key={`like-${r.candidate_id}`} className="p-3 border rounded-md flex items-center justify-between">
                  <div>
                    <div className="font-medium text-foreground">{r.research_topic}</div>
                    <div className="text-sm text-muted-foreground">{r.candidate_name}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => {
                    try { localStorage.setItem('selectedCandidateId', r.candidate_id); } catch {}
                    window.location.reload();
                  }}>詳細を見る</Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>参加希望した発表</CardTitle>
          <CardDescription>あなたが「参加希望」を表明した研究の一覧です。</CardDescription>
        </CardHeader>
        <CardContent>
          {participateList.length === 0 ? (
            <p className="text-sm text-muted-foreground">まだ「参加希望」した発表はありません。</p>
          ) : (
            <div className="space-y-2">
              {participateList.map((r) => (
                <div key={`part-${r.candidate_id}`} className="p-3 border rounded-md flex items-center justify-between">
                  <div>
                    <div className="font-medium text-foreground">{r.research_topic}</div>
                    <div className="text-sm text-muted-foreground">{r.candidate_name}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => {
                    try { localStorage.setItem('selectedCandidateId', r.candidate_id); } catch {}
                    window.location.reload();
                  }}>詳細を見る</Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>自分が発言した研究・スレッド</CardTitle>
          <CardDescription>あなたがコメントした研究とトピックの一覧です。</CardDescription>
        </CardHeader>
        <CardContent>
          {myThreads.length === 0 ? (
            <p className="text-sm text-muted-foreground">まだ発言はありません。発表一覧からディスカッションに参加してみましょう。</p>
          ) : (
            <div className="space-y-3">
              {myThreads.map((t) => (
                <div key={`${t.candidate_id}-${t.topic}`} className="p-3 border rounded-md hover:bg-muted/40">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">{new Date(t.last_commented_at).toLocaleString('ja-JP')}</div>
                    <Button size="sm" variant="outline" onClick={() => {
                      try { localStorage.setItem('selectedCandidateId', t.candidate_id); } catch {}
                      window.location.reload();
                    }}>詳細を見る</Button>
                  </div>
                  <div className="font-medium text-foreground">{t.research_topic}（{t.candidate_name}）</div>
                  <div className="text-sm text-muted-foreground">トピック: {t.topic === 'general' ? '一般' : t.topic} ／ 発言数: {t.comment_count}</div>
                </div>
              ))}
              <p className="text-xs text-muted-foreground">該当スレッドを開くには、ヘッダーの「発表一覧」から対象の研究を選んでください。</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
